import axios from 'axios';

const api = axios.create({
    baseURL: '',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor - add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export default api;

// Auth APIs
export const authApi = {
    login: (username, password) =>
        api.post('/api/auth/login', {username, password}),
    logout: () =>
        api.post('/api/auth/logout'),
    changePassword: (currentPassword, newPassword) =>
        api.put('/api/auth/password', {currentPassword, newPassword}),
};

// Build APIs
export const buildApi = {
    getOptions: () =>
        api.get('/api/build/options/all'),
    getTags: (productId) =>
        api.get(`/api/build/products/${productId}/tags`),
    getBranches: (productId) =>
        api.get(`/api/build/products/${productId}/branches`),
    getVersions: (productId) =>
        api.get(`/api/build/products/${productId}/versions`),
    prefetchRecent: () =>
        api.post('/api/build/prefetch'),
    getProfiles: (productId) =>
        api.get(`/api/build/products/${productId}/profiles`),
    getDeployTargets: (productId, type, name) =>
        api.get(`/api/build/products/${productId}/deploy-targets`, {params: {type, name}}),
    getAllDeployTargets: (productId) =>
        api.get(`/api/build/products/${productId}/deploy-targets/all`),
    getInstallCriteria: () =>
        api.get('/api/build/install-criteria'),
    judgeInstallCriteria: (inputs) =>
        api.post('/api/build/install-criteria/judge', {inputs}),
    createBuild: (data) =>
        api.post('/api/build', data),
    getBuild: (id) =>
        api.get(`/api/build/${id}`),
    getBuilds: (limit = 10, page = 1, filters = {}) => {
        const params = new URLSearchParams({limit, page});
        if (filters.productId) params.append('productId', filters.productId);
        if (filters.profile) params.append('profile', filters.profile);
        if (filters.mode) params.append('mode', filters.mode);
        if (filters.requestedBy) params.append('requestedBy', filters.requestedBy);
        if (filters.status) params.append('status', Array.isArray(filters.status) ? filters.status.join(',') : filters.status);
        return api.get(`/api/builds?${params.toString()}`);
    },
    getFilterOptions: () =>
        api.get('/api/builds/filters'),
    cancelBuild: (id) =>
        api.delete(`/api/build/${id}`),
    deleteBuild: (id) =>
        api.delete(`/api/build/${id}/delete`),
    downloadBuild: async (id) => {
        try {
            const response = await api.get(`/api/build/${id}/download`, {responseType: 'blob'});
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'build.zip';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+?)"?$/);
                if (match) fileName = match[1];
            }
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            const data = err.response?.data;
            let message = '다운로드에 실패했습니다.';
            if (data instanceof Blob) {
                try {
                    const text = await data.text();
                    const json = JSON.parse(text);
                    message = json.error || message;
                } catch (_) {
                }
            } else if (data?.error) {
                message = data.error;
            }
            alert(message);
        }
    },
};

// Config APIs
export const configApi = {
    encryptValues: (values, secretKey) =>
        api.post('/api/config/encrypt', {values, secretKey}),
};

// Admin APIs
export const adminApi = {
    // Users
    getUsers: () =>
        api.get('/api/admin/users'),
    createUser: (data) =>
        api.post('/api/admin/users', data),
    updateUser: (id, data) =>
        api.put(`/api/admin/users/${id}`, data),
    deleteUser: (id) =>
        api.delete(`/api/admin/users/${id}`),

    // Products
    getProducts: () =>
        api.get('/api/admin/products'),
    getProduct: (id) =>
        api.get(`/api/admin/products/${id}`),
    createProduct: (data) =>
        api.post('/api/admin/products', data),
    updateProduct: (id, data) =>
        api.put(`/api/admin/products/${id}`, data),
    deleteProduct: (id) =>
        api.delete(`/api/admin/products/${id}`),

    // Product Profiles
    getProductProfiles: (productId) =>
        api.get(`/api/admin/products/${productId}/profiles`),
    createProductProfile: (productId, data) =>
        api.post(`/api/admin/products/${productId}/profiles`, data),
    deleteProductProfile: (productId, profileId) =>
        api.delete(`/api/admin/products/${productId}/profiles/${profileId}`),

    // Env Vars
    getEnvVars: () =>
        api.get('/api/admin/env'),
    saveEnvVars: (envVars) =>
        api.put('/api/admin/env', {envVars}),

    // Remote Target
    getRemoteTarget: () =>
        api.get('/api/admin/remote-target'),
    saveRemoteTarget: (target) =>
        api.put('/api/admin/remote-target', target),
};
